import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { LocalStorageService } from '../shared/services/local-storage.service';
import { CalloutType, FeatureCallout } from './feature-callout.model';

@Injectable({
    providedIn: 'root'
})
export class FeatureCalloutService {

    public callouts: Observable<Array<FeatureCallout>>;

    private readonly _callouts: BehaviorSubject<Array<FeatureCallout>>;
    private static readonly calloutDismissedKeyPrefix = '_FeatureCalloutDismissed';
    private static readonly calloutCompletedKeyPrefix = '_FeatureCalloutCompleted';

    constructor(
        private readonly localStorageService: LocalStorageService
    ) {
        this._callouts = new BehaviorSubject<Array<FeatureCallout>>([]);
        this.callouts = this._callouts.asObservable();
    }

    public addCallout(callout: FeatureCallout): void {
        if (!callout.isValid() || this.calloutDismissed(callout)) {
            return;
        }

        if (!callout.completed && this.calloutCompleted(callout)) {
            callout.completed = true;
        }
        this._callouts.next(this._callouts.getValue().concat(callout));
    }

    public addCallouts(newCallouts: Array<FeatureCallout>): void {
        const calloutsToAdd =
            newCallouts.filter(c => {
                if (!c.isValid() || this.calloutDismissed(c)) {
                    return false;
                }
                if (!c.completed && this.calloutCompleted(c)) {
                    c.completed = true;
                }
                return true;
            });
        this._callouts.next(this._callouts.getValue().concat(calloutsToAdd));
    }

    public clearCallouts(): void {
        this._callouts.next([]);
    }

    public dismissCallout(callout: FeatureCallout): void {
        if (callout.persistCompletion) {
            this.localStorageService.set(this.getCompletedKey(callout.calloutType), true);
        }

        if (callout.persistDismissal) {
            this.localStorageService.set(this.getDismissKey(callout.calloutType), true);
        }
    }

    private calloutDismissed(callout: FeatureCallout): boolean {
        if (!callout.persistDismissal) {
            return false;
        }

        const storageKey = this.getDismissKey(callout.calloutType);
        return !!this.localStorageService.get(storageKey);
    }

    private calloutCompleted(callout: FeatureCallout): boolean {
        if (!callout.persistCompletion) {
            return false;
        }

        const storageKey = this.getCompletedKey(callout.calloutType);
        return !!this.localStorageService.get(storageKey);
    }

    private getDismissKey(calloutType: CalloutType): string {
        return `${FeatureCalloutService.calloutDismissedKeyPrefix}_${calloutType}`;
    }

    private getCompletedKey(calloutType: CalloutType): string {
        return `${FeatureCalloutService.calloutCompletedKeyPrefix}_${calloutType}`;
    }
}
